import { database } from './core';
import { ref, query, orderByChild, equalTo, onValue } from 'firebase/database';
import {
  updateTrainingsFromFirebase,
  updateTrainingAction,
  removeTraining,
  syncComplete
} from '../../store/slices/trainingSlice';
import { AppDispatch } from '../../store';
import { Training, removeTrainingFromCache } from './training.service';
import { processPendingActions } from './offlinePendingActions';
import logger from '../../utils/logger';

/**
 * Shared training-sync subscription. useTraining() is used by ~16 components,
 * and each previously attached its own full-history listener on /trainings.
 * Ref-count a single listener per uid so exactly one exists per session; all
 * consumers dispatch into the same Redux store, so the data shown is identical.
 */
let activeSync: { uid: string; unsubscribe: () => void; refCount: number } | null = null;

const releaseTrainingSync = (uid: string): (() => void) => {
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (activeSync && activeSync.uid === uid) {
      activeSync.refCount -= 1;
      if (activeSync.refCount <= 0) {
        activeSync.unsubscribe();
        activeSync = null;
      }
    }
  };
};

/**
 * Set up realtime sync for a user's training data
 * @param uid User ID to sync data for
 * @param dispatch Redux dispatch function
 * @returns Function to unsubscribe from updates
 */
export const setupTrainingSync = (uid: string, dispatch: AppDispatch): (() => void) => {
  // Reuse the existing listener for this user; only the first caller creates it
  if (activeSync && activeSync.uid === uid) {
    activeSync.refCount += 1;
    return releaseTrainingSync(uid);
  }
  // A listener for a different user is stale (user switched) — tear it down
  if (activeSync) {
    activeSync.unsubscribe();
    activeSync = null;
  }

  const unsubscribe = createTrainingSyncListener(uid, dispatch);
  activeSync = { uid, unsubscribe, refCount: 1 };
  return releaseTrainingSync(uid);
};

const createTrainingSyncListener = (uid: string, dispatch: AppDispatch): (() => void) => {

  logger.info(`Setting up training sync for user: ${uid}`);
  
  try {
    // Setup listener for trainings
    const trainingsRef = ref(database, 'trainings');
    const trainingsQuery = query(trainingsRef, orderByChild('owner'), equalTo(uid));
    
    // Mark sync as started for UI feedback
    dispatch({ type: 'training/syncStarted' });
    
    // Keep track of retry count for error handling
    let retryCount = 0;
    const MAX_RETRIES = 3;
    
    const setupListener = () => {
      const trainingsListener = onValue(trainingsQuery, async (snapshot) => {
        if (!snapshot.exists()) {
          logger.info('No training data found for user');
          // Return empty state when no data exists
          dispatch(updateTrainingsFromFirebase([]));
          dispatch(syncComplete());
          return;
        }
        
        const trainings: Training[] = [];
        
        snapshot.forEach((childSnapshot) => {
          trainings.push({
            id: childSnapshot.key!,
            ...childSnapshot.val()
          });
        });
        
        logger.info(`Received ${trainings.length} trainings from Firebase`);
        
        // Update the store with Firebase data
        dispatch(updateTrainingsFromFirebase(trainings));
        
        // Mark sync as complete
        dispatch(syncComplete());
        
        // Reset retry count on success
        retryCount = 0;
      }, (error) => {
        logger.error('Error in training sync:', error);
        
        // If we have retries left, try again with exponential backoff
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          const backoffTime = Math.pow(2, retryCount) * 1000; // Exponential backoff
          logger.info(`Retrying training sync in ${backoffTime}ms (attempt ${retryCount}/${MAX_RETRIES})`);
          
          setTimeout(() => {
            // Unsubscribe first to prevent duplicate listeners
            trainingsListener();  // onValue returns the unsubscribe function
            setupListener();
          }, backoffTime);
        } else {
          // Max retries reached, dispatch error
          dispatch({ 
            type: 'training/syncError', 
            payload: { message: error.message || 'Error syncing training data' } 
          });
        }
      });
      
      // Return unsubscribe function
      return () => {
        logger.info('Unsubscribing from training updates');
        trainingsListener();  // onValue returns the unsubscribe function
      };
    };
    
    // Initial setup
    return setupListener();
  } catch (error) {
    logger.error('Error setting up training sync:', error);
    
    // Dispatch error for UI feedback
    dispatch({ 
      type: 'training/syncError', 
      payload: { message: error instanceof Error ? error.message : 'Unknown error setting up training sync' } 
    });
    
    return () => {}; // Return empty function if setup fails
  }
};

/**
 * Subscribe to a specific training to get realtime updates
 * @param trainingId Training ID to subscribe to
 * @param dispatch Redux dispatch function
 * @returns Function to unsubscribe from updates
 */
export const subscribeToTrainingUpdates = (
  trainingId: string,
  dispatch: AppDispatch
): (() => void) => {
  // Temporarily disable log to prevent console issues
  // logger.info(`Setting up subscription for training: ${trainingId}`);
  
  try {
    const trainingRef = ref(database, `trainings/${trainingId}`);
    
    // Keep track of retry count for error handling
    let retryCount = 0;
    const MAX_RETRIES = 3;
    
    // Mark that we're loading this specific training
    dispatch({ 
      type: 'training/fetchTrainingStart', 
      payload: { id: trainingId } 
    });
    
    const setupListener = () => {
      const trainingListener = onValue(trainingRef, (snapshot) => {
        if (!snapshot.exists()) {
          logger.info(`Training ${trainingId} was deleted or not found`);
          // Clear from both Redux and service-level cache to prevent loops
          removeTrainingFromCache(trainingId);
          dispatch(removeTraining(trainingId));
          return;
        }
        
        const training: Training = {
          id: trainingId,
          ...snapshot.val()
        };
        
        logger.info(`Received update for training ${trainingId}`);
        
        // Check if training has pending changes before overwriting
        // Instead of importing store directly (which causes circular imports),
        // we'll use a function to check pending changes from passed arguments
        const pendingChanges = [] as any[]; // This will be filled by parent functions
        const hasPendingChanges = pendingChanges.some(
          (change: any) => change.entityId === trainingId && change.status === 'pending'
        );
        
        if (hasPendingChanges) {
          logger.info(`Training ${trainingId} has pending changes, not overwriting with server data`);
          // Just update timestamps and server-specific fields
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { dateModified, likes, ..._otherFields } = training;
          
          // Only apply server-controlled fields, preserve local changes
          // Get the modified data as a valid Training object (containing id field)
          const modifiedTraining: Training = {
            ...(training as Training),
            // Only keep these fields from the server version
            dateModified,
            likes
          };
          
          // Update with valid Training object
          dispatch(updateTrainingAction({ 
            id: trainingId,
            data: modifiedTraining
          }));
        } else {
          // No pending changes, safe to overwrite completely
          dispatch(updateTrainingAction({ 
            id: trainingId,
            data: training
          }));
        }
        
        // Reset retry count on success
        retryCount = 0;
      }, (error) => {
        logger.error(`Error in training ${trainingId} sync:`, error);
        
        // If we have retries left, try again with exponential backoff
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          const backoffTime = Math.pow(2, retryCount) * 1000; // Exponential backoff
          logger.info(`Retrying training ${trainingId} sync in ${backoffTime}ms (attempt ${retryCount}/${MAX_RETRIES})`);
          
          setTimeout(() => {
            // Unsubscribe first to prevent duplicate listeners
            trainingListener();  // onValue returns the unsubscribe function
            setupListener();
          }, backoffTime);
        } else {
          // Max retries reached, dispatch error
          dispatch({ 
            type: 'training/fetchTrainingError', 
            payload: { 
              id: trainingId,
              error: error.message || `Error syncing training ${trainingId}`
            } 
          });
        }
      });
      
      // Return unsubscribe function
      return () => {
        logger.info(`Unsubscribing from training ${trainingId} updates`);
        trainingListener();  // onValue returns the unsubscribe function
      };
    };
    
    // Initial setup
    return setupListener();
  } catch (error) {
    logger.error(`Error setting up training ${trainingId} sync:`, error);
    
    // Dispatch error for UI feedback
    dispatch({ 
      type: 'training/fetchTrainingError', 
      payload: { 
        id: trainingId, 
        error: error instanceof Error ? error.message : `Unknown error setting up training ${trainingId} sync`
      } 
    });
    
    return () => {}; // Return empty function if setup fails
  }
};

/**
 * Set up a listener for training statistics updates
 * @param uid User ID to monitor stats for
 * @param dispatch Redux dispatch function
 * @returns Function to unsubscribe from updates
 */
export const setupTrainingStatsSync = (
  uid: string,
  dispatch: AppDispatch,
  onStatsUpdate: (stats: any) => void
): (() => void) => {
  logger.info(`Setting up training stats sync for user: ${uid}`);
  
  try {
    // Setup listener for userStats
    const statsRef = ref(database, `userStats/${uid}`);
    
    // Keep track of retry count for error handling
    let retryCount = 0;
    const MAX_RETRIES = 3;
    
    const setupListener = () => {
      const statsListener = onValue(statsRef, (snapshot) => {
        if (!snapshot.exists()) {
          logger.info('No user stats found');
          onStatsUpdate(null);
          return;
        }
        
        const stats = snapshot.val();
        logger.info('Received user stats update');
        onStatsUpdate(stats);
        
        // Reset retry count on success
        retryCount = 0;
      }, (error) => {
        logger.error('Error in training stats sync:', error);
        
        // If we have retries left, try again with exponential backoff
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          const backoffTime = Math.pow(2, retryCount) * 1000; // Exponential backoff
          logger.info(`Retrying stats sync in ${backoffTime}ms (attempt ${retryCount}/${MAX_RETRIES})`);
          
          setTimeout(() => {
            // Unsubscribe first to prevent duplicate listeners
            statsListener();  // onValue returns the unsubscribe function
            setupListener();
          }, backoffTime);
        } else {
          // Max retries reached, notify callback
          onStatsUpdate({ error: 'Failed to sync stats after multiple attempts' });
        }
      });
      
      // Return unsubscribe function
      return () => {
        logger.info('Unsubscribing from training stats updates');
        statsListener();  // onValue returns the unsubscribe function
      };
    };
    
    // Initial setup
    return setupListener();
  } catch (error) {
    logger.error('Error setting up training stats sync:', error);
    
    // Notify callback about the error
    onStatsUpdate({ error: error instanceof Error ? error.message : 'Unknown error setting up stats sync' });
    
    return () => {}; // Return empty function if setup fails
  }
};

/**
 * Handle offline/online events and reconnect Firebase listeners as needed
 * @param uid User ID
 * @param dispatch Redux dispatch function
 */
/**
 * Handles conflict resolution for training data after being offline
 * Uses a more sophisticated approach than Firebase's built-in last-writer-wins strategy,
 * with special handling for optimistic updates and pending changes.
 * 
 * @param uid User ID
 * @param dispatch Redux dispatch function
 * @param localData Local training data that might have changes
 */
export const handleSyncConflicts = async (
  uid: string, 
  dispatch: AppDispatch,
  localData: {
    trainings: Record<string, Training>,
    trainingIds: string[],
    pendingChanges?: any[]
  }
): Promise<void> => {
  logger.info('Handling potential sync conflicts after offline period');
  
  try {
    // Log the state for debugging
    logger.info(`Local state has ${Object.keys(localData.trainings).length} trainings`);
    
    // Notify UI that conflict resolution is in progress
    dispatch({ type: 'training/syncConflictResolutionStarted' });
    
    // If we have explicit pending changes, process them
    const hasPendingChanges = localData.pendingChanges && localData.pendingChanges.length > 0;
    
    if (hasPendingChanges) {
      logger.info(`Found ${localData.pendingChanges!.length} pending changes to process`);
      
      // Group changes by entity ID for more efficient processing
      const changesByEntityId = localData.pendingChanges!.reduce((acc, change) => {
        if (!acc[change.entityId]) {
          acc[change.entityId] = [];
        }
        acc[change.entityId].push(change);
        return acc;
      }, {} as Record<string, any[]>);
      
      logger.info(`Grouped changes by entity: ${Object.keys(changesByEntityId).length} affected entities`);
      
      // Process any pending changes that failed while offline
      // The actual processing happens in processPendingActions() called earlier
    }
    
    // Get current server data to compare with local data
    const trainingsRef = ref(database, 'trainings');
    const trainingsQuery = query(trainingsRef, orderByChild('owner'), equalTo(uid));
    
    logger.info('Fetching latest data from server for conflict resolution');
    
    // Add a small delay before fetching to allow Firebase to finish its own sync
    await new Promise<void>((resolve) => {
      setTimeout(async () => {
        try {
          // Fetch fresh data to enable intelligent conflict resolution
          const snapshot = await new Promise<any>((resolveSnapshot, rejectSnapshot) => {
            onValue(trainingsQuery, resolveSnapshot, rejectSnapshot, { onlyOnce: true });
          });
          
          if (!snapshot.exists()) {
            logger.info('No training data found on server after reconnection');
            
            if (Object.keys(localData.trainings).length > 0) {
              logger.info('Local data exists but server has none - keeping local data and syncing to server');
              // If we have local data but server doesn't, our changes are still being processed
              // We'll let the normal Firebase sync process handle this
            } else {
              dispatch(updateTrainingsFromFirebase([]));
            }
            
            resolve();
            return;
          }
          
          const serverTrainings: Record<string, Training> = {};
          
          snapshot.forEach((childSnapshot: any) => {
            const training = {
              id: childSnapshot.key!,
              ...childSnapshot.val()
            };
            serverTrainings[training.id] = training;
          });
          
          logger.info(`Received ${Object.keys(serverTrainings).length} trainings from server for conflict resolution`);
          
          // Perform intelligent conflict resolution
          if (hasPendingChanges) {
            // For entities with pending changes, we need more careful conflict resolution
            const updatedTrainings: Training[] = [];
            
            // Process all trainings to resolve conflicts
            for (const id of new Set([...Object.keys(localData.trainings), ...Object.keys(serverTrainings)])) {
              const localTraining = localData.trainings[id];
              const serverTraining = serverTrainings[id];
              
              // Check if this entity has pending changes
              const hasPendingChangesForEntity = localData.pendingChanges?.some(
                change => change.entityId === id && change.status === 'pending'
              );
              
              // Different resolution strategies based on situation
              if (!localTraining && serverTraining) {
                // Server has training we don't - add it
                updatedTrainings.push(serverTraining);
              } 
              else if (localTraining && !serverTraining) {
                // We have training server doesn't
                // Check if training is pending sync (using any type since it might be a custom property)
                if ((localTraining as any)._pendingSync || hasPendingChangesForEntity) {
                  // This is a new local training waiting to be synced - keep it
                  // It will be uploaded to server by pending actions
                  updatedTrainings.push(localTraining);
                }
                // Otherwise it was deleted on server, which takes precedence
              }
              else if (localTraining && serverTraining) {
                // Both have the training - need to merge carefully
                // Check if training is pending sync (using any type since it might be a custom property)
                if ((localTraining as any)._pendingSync || hasPendingChangesForEntity) {
                  // Local changes that haven't synced yet - merge with server data
                  // Keep local editable fields, take server fields for things like likes
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { likes, ..._serverFields } = serverTraining;
                  
                  // Fields user would have edited locally
                  const { note, title, type, location, instructor, techniques, rounds, taggedFriends } = localTraining; // Use correct field name
                  
                  // Create merged version
                  const mergedTraining = {
                    ...serverTraining, // Base is server version
                    // User-edited fields from local version take precedence
                    note, title, type, location, instructor, techniques, rounds, taggedFriends, // Use correct field name
                    // But keep server's social data
                    likes
                  };
                  
                  updatedTrainings.push(mergedTraining);
                } else {
                  // No pending changes - server version wins
                  updatedTrainings.push(serverTraining);
                }
              }
            }
            
            logger.info(`After conflict resolution, updating store with ${updatedTrainings.length} trainings`);
            dispatch(updateTrainingsFromFirebase(updatedTrainings));
          } else {
            // No pending changes - simpler update
            const trainings = Object.values(serverTrainings);
            dispatch(updateTrainingsFromFirebase(trainings));
          }
          
          // Notify UI that conflict resolution is complete
          dispatch({ type: 'training/syncConflictResolutionComplete' });
          dispatch(syncComplete());
          
          resolve();
        } catch (error) {
          logger.error('Error during conflict resolution:', error);
          dispatch({ 
            type: 'training/syncError', 
            payload: { message: error instanceof Error ? error.message : 'Error during conflict resolution' } 
          });
          resolve(); // Still resolve to continue execution
        }
      }, 1500); // Slightly longer delay (1.5s) to allow Firebase's own sync to complete
    });
    
  } catch (error: any) {
    logger.error('Error handling sync conflicts:', error);
    dispatch({ 
      type: 'training/syncError', 
      payload: { message: error.message || 'Unknown error during conflict resolution' } 
    });
  }
};

/**
 * Sets up offline reconnection handlers with enhanced conflict resolution
 * @param uid User ID
 * @param dispatch Redux dispatch function
 * @returns Teardown function that unsubscribes the connection listener,
 *          removes the window online/offline handlers, and clears pending
 *          timers - previously this returned void, so every login/user-switch
 *          leaked one RTDB subscription and two window listeners
 */
export const setupOfflineReconnection = (uid: string, dispatch: AppDispatch): (() => void) => {
  let syncUnsubscribe: (() => void) | null = null;
  let isOffline = false;
  let offlineTimestamp: number | null = null;
  let reconnectionAttempts = 0;
  const MAX_RECONNECTION_ATTEMPTS = 5;
  let reconnectionRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let startupCheckTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // This function will be replaced with a parameter passed from the hook
  // to avoid circular dependencies
  const getLocalState = () => {
    // Return empty state as we'll get the actual state from the hook
    return {
      trainings: {},
      trainingIds: [],
      pendingChanges: []
    };
  };
  
  // Setup connection status monitoring with Firebase
  // Returns the onValue unsubscribe so teardown can detach the listener
  const monitorConnectionState = (): (() => void) => {
    const connectedRef = ref(database, '.info/connected');
    return onValue(connectedRef, (snap) => {
      const connected = snap.val() === true;
      
      if (connected) {
        logger.info('Firebase database connection established');
        // Reset reconnection counter when we have a solid connection
        reconnectionAttempts = 0;
        
        // Store flag in localStorage for app to check on startup
        localStorage.setItem('marune_firebase_connected', 'true');
        
        // Dispatch connection state to Redux if we weren't already connected
        if (isOffline) {
          dispatch({ type: 'training/setOnlineStatus', payload: true });
          isOffline = false;
        }
      } else {
        logger.info('Firebase database disconnected');
        localStorage.setItem('marune_firebase_connected', 'false');
        
        // Don't immediately set offline - might be a temporary blip
        // The offline event listener will handle longer disconnections
      }
    });
  };
  
  // Start monitoring connection state
  const connectionUnsubscribe = monitorConnectionState();

  // Setup online detection (named handler so teardown can remove it)
  const handleOnline = async () => {
    logger.info('App is back online, reconnecting Firebase listeners');
    dispatch({ type: 'training/setOnlineStatus', payload: true });
    
    // Get the duration of the offline period
    const offlineDuration = offlineTimestamp ? Date.now() - offlineTimestamp : 0;
    logger.info(`Device was offline for ${Math.round(offlineDuration / 1000)} seconds`);
    
    // Display a notification for user awareness
    if (offlineDuration > 10000) { // Only for disconnections longer than 10 seconds
      // Dispatch notification event for UI to display
      dispatch({ 
        type: 'training/connectionStateChanged', 
        payload: { 
          isOnline: true, 
          offlineDuration: Math.round(offlineDuration / 1000) 
        } 
      });
    }
    
    // Get local state to check for conflicts
    const localState = getLocalState();
    
    // Handle reconnection with retry logic
    const handleReconnection = async () => {
      try {
        // Process any pending actions first
        await processPendingActions(uid);
        
        // Then handle any remaining conflicts
        await handleSyncConflicts(uid, dispatch, localState);
        
        // Unsubscribe first if needed
        if (syncUnsubscribe) {
          syncUnsubscribe();
        }
        
        // Reconnect with fresh data
        syncUnsubscribe = setupTrainingSync(uid, dispatch);
        
        // Reset offline state
        isOffline = false;
        offlineTimestamp = null;
        reconnectionAttempts = 0;
        
        logger.info('Reconnection successful');
      } catch (error) {
        logger.error('Error during reconnection:', error);
        
        // Increment reconnection attempts
        reconnectionAttempts++;
        
        if (reconnectionAttempts < MAX_RECONNECTION_ATTEMPTS) {
          // Calculate backoff time with jitter to prevent thundering herd problem
          const baseDelay = Math.pow(2, reconnectionAttempts) * 1000; // Exponential backoff
          const jitter = Math.random() * 1000; // Random jitter up to 1 second
          const delayMs = baseDelay + jitter;
          
          logger.info(`Reconnection attempt ${reconnectionAttempts}/${MAX_RECONNECTION_ATTEMPTS} failed, retrying in ${Math.round(delayMs/1000)} seconds`);

          // Try again after delay (tracked so teardown can cancel it)
          reconnectionRetryTimeoutId = setTimeout(handleReconnection, delayMs);
        } else {
          logger.error(`Max reconnection attempts (${MAX_RECONNECTION_ATTEMPTS}) reached, giving up`);
          
          // Notify UI of reconnection failure
          dispatch({ 
            type: 'training/syncError', 
            payload: { 
              message: 'Failed to reconnect after multiple attempts. Please check your connection and try again.' 
            } 
          });
        }
      }
    };
    
    // Start reconnection process if we were offline
    if (isOffline) {
      handleReconnection();
    }
  };
  window.addEventListener('online', handleOnline);

  // Setup offline detection (named handler so teardown can remove it)
  const handleOffline = () => {
    logger.info('App is offline, Firebase listeners will pause');
    
    // Update state tracking
    offlineTimestamp = Date.now();
    isOffline = true;
    
    // Notify Redux about offline state
    dispatch({ type: 'training/setOnlineStatus', payload: false });
    
    // Dispatch notification event for UI to display
    dispatch({ 
      type: 'training/connectionStateChanged', 
      payload: { isOnline: false } 
    });
    
    // Firebase handles offline mode automatically, but we track it for conflict resolution
  };
  window.addEventListener('offline', handleOffline);

  // Handle case where page was reloaded while offline
  if (!navigator.onLine) {
    logger.info('Page loaded while offline, setting offline state');
    isOffline = true;
    offlineTimestamp = Date.now();
    dispatch({ type: 'training/setOnlineStatus', payload: false });
  } else {
    // Check if we have pending changes even though we're online
    // This can happen if the page was closed while offline and reopened online
    startupCheckTimeoutId = setTimeout(async () => {
      const localState = getLocalState();
      if (localState.pendingChanges && localState.pendingChanges.length > 0) {
        logger.info(`Found ${localState.pendingChanges.length} pending changes on startup, processing`);
        try {
          await processPendingActions(uid);
          await handleSyncConflicts(uid, dispatch, localState);
        } catch (error) {
          logger.error('Error processing pending changes on startup:', error);
        }
      }
    }, 3000); // Wait a bit for app to stabilize before checking
  }

  // Teardown function - called from useTraining's effect cleanup
  return () => {
    logger.info('Tearing down offline reconnection handlers');
    connectionUnsubscribe();
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    if (reconnectionRetryTimeoutId) {
      clearTimeout(reconnectionRetryTimeoutId);
      reconnectionRetryTimeoutId = null;
    }
    if (startupCheckTimeoutId) {
      clearTimeout(startupCheckTimeoutId);
      startupCheckTimeoutId = null;
    }
    // Release any sync subscription created during a reconnection cycle
    if (syncUnsubscribe) {
      syncUnsubscribe();
      syncUnsubscribe = null;
    }
  };
};

/**
 * Force a sync of all data for a user with enhanced error handling and conflict resolution
 * @param uid User ID
 * @param dispatch Redux dispatch function
 */
export const forceSync = async (uid: string, dispatch: AppDispatch): Promise<void> => {
  logger.info('Forcing sync for user:', uid);
  
  try {
    // Notify UI that sync has started
    dispatch({ type: 'training/syncStarted' });
    
    // First process any pending changes
    logger.info('Processing any pending actions before fetching latest data');
    await processPendingActions(uid);
    
    // We'll get the local state from arguments instead of importing the store directly
    // This avoids circular dependencies
    
    // Get current local state for conflict resolution if needed
    // This will be populated from the useTraining hook
    const localData = {
      trainings: {},
      trainingIds: [],
      pendingChanges: []
    };
    
    const hasPendingChanges = localData.pendingChanges.length > 0;
    
    // If we still have pending changes after processing, perform conflict resolution
    if (hasPendingChanges) {
      logger.info(`Still have ${localData.pendingChanges.length} pending changes after processing - using conflict resolution`);
      await handleSyncConflicts(uid, dispatch, localData);
      return; // handleSyncConflicts will update the store and mark as synced
    }
    
    // No more pending changes - just get fresh data from Firebase
    logger.info('Getting fresh data from Firebase');
    const trainingsRef = ref(database, 'trainings');
    const trainingsQuery = query(trainingsRef, orderByChild('owner'), equalTo(uid));
    
    try {
      // Using promise-based approach for better error handling
      const snapshot = await new Promise<any>((resolve, reject) => {
        onValue(trainingsQuery, resolve, reject, { onlyOnce: true });
      });
      
      if (!snapshot.exists()) {
        logger.info('No training data found during force sync');
        dispatch(updateTrainingsFromFirebase([]));
        dispatch(syncComplete());
        return;
      }
      
      const trainings: Training[] = [];
      
      snapshot.forEach((childSnapshot: any) => {
        trainings.push({
          id: childSnapshot.key!,
          ...childSnapshot.val()
        });
      });
      
      logger.info(`Force sync: received ${trainings.length} trainings from Firebase`);
      
      // Apply transformation to ensure consistent data format
      const processedTrainings = trainings.map(training => {
        // Ensure required fields are present
        return {
          ...training,
          // Add any missing required fields with defaults
          dateCreated: training.dateCreated || Date.now(),
          dateModified: training.dateModified || Date.now(),
          // Remove any temporary flags
          _pendingSync: false,
          _offline: false
        };
      });
      
      // Update the store
      dispatch(updateTrainingsFromFirebase(processedTrainings));
      
      // Mark as synced
      dispatch(syncComplete());
      
      // Notify UI that sync has completed
      dispatch({ type: 'training/syncSuccess' });
      
      logger.info('Force sync completed successfully');
    } catch (error) {
      logger.error('Error fetching data during force sync:', error);
      dispatch({ 
        type: 'training/syncError', 
        payload: { message: error instanceof Error ? error.message : 'Error fetching data during force sync' } 
      });
    }
  } catch (error: any) {
    logger.error('Error during force sync:', error);
    dispatch({ 
      type: 'training/syncError', 
      payload: { message: error.message || 'Unknown error during force sync' } 
    });
    throw error;
  }
};

const trainingSyncService = {
  setupTrainingSync,
  subscribeToTrainingUpdates,
  setupTrainingStatsSync,
  setupOfflineReconnection,
  handleSyncConflicts,
  forceSync
};

export default trainingSyncService;